import { ArrowRight, Gift } from "lucide-react";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { SITE_URL } from "../../config/site";
import FeaturedProducts from "../../components/FeaturedProducts";
import SEO from "../../components/SEO";
import StructuredData from "../../components/StructuredData";
import Header from "../../components/layout/Header";
import Footer from "../../components/layout/Footer";
import "./HomePage.css";

const FRAME_FEATURES = [
  {
    eyebrow: "Modelos y colores",
    title: "Marcos individuales",
    text: "Prueba una foto, cambia el modelo y el color, y encuentra el marco que mejor encaja con ese recuerdo que quieres tener siempre a la vista.",
    imageSrc:
      "/images/frame-gallery/frame-gallery-individuales/frame-gallery-individuales-composicion.webp",
    imageAlt: "Marcos individuales con imanes personalizados sobre una nevera",
    videoSrc: "/videos/marcos.mp4",
    videoAlt: "Video mostrando marcos individuales para imanes personalizados",
    link: "/marcos-individuales",
    cta: "Probar marcos individuales",
    layout: ["image", "video", "text"],
  },
  {
    eyebrow: "Dos fotos juntas",
    title: "Marco Duo",
    text: "Dos recuerdos juntos en una pieza sencilla, equilibrada y muy visual. Ideal para parejas de fotos, hermanos, mascotas o momentos que van de la mano.",
    imageSrc:
      "/images/frame-gallery/frame-gallery-duo/frame-gallery-duo-composicion.webp",
    imageAlt: "Marco Duo con dos fotografías decorando un comedor",
    videoSrc: "/videos/marco-duo.mp4",
    videoAlt: "Video mostrando Marco Duo para imanes personalizados",
    link: "/marco-duo",
    cta: "Probar Marco Duo",
    layout: ["text", "image", "video"],
  },
  {
    eyebrow: "Cuatro momentos",
    title: "Marco Mosaico",
    text: "Cuatro fotos reunidas en una composición con más presencia. Una forma muy bonita de crear una pequeña historia visual en casa.",
    imageSrc:
      "/images/frame-gallery/frame-gallery-mosaico/frame-gallery-mosaico-composicion.webp",
    imageAlt: "Marco Mosaico con cuatro fotografías sobre una estantería",
    videoSrc: "/videos/marco-mosaico.mp4",
    videoAlt: "Video mostrando Marco Mosaico para imanes personalizados",
    link: "/marco-mosaico",
    cta: "Probar Marco Mosaico",
    layout: ["video", "text", "image"],
  },
  {
    eyebrow: "Cuatro fotos en fila",
    title: "Marco Secuencia",
    text: "Cuatro fotos en línea para contar una secuencia, un viaje, una evolución o varios momentos de una misma historia.",
    imageSrc:
      "/images/frame-gallery/frame-gallery-secuencia/frame-gallery-secuencia-composicion.webp",
    imageAlt: "Marco Secuencia decorando una estantería",
    videoSrc: "/videos/marco-secuencia.mp4",
    videoAlt: "Video mostrando Marco Secuencia para imanes personalizados",
    link: "/marco-secuencia",
    cta: "Probar Marco Secuencia",
    layout: ["image", "text", "video"],
  },
];

const COLLAGE_FEATURE = {
  eyebrow: "Collage magnético",
  title: "Una foto especial convertida en muchas piezas",
  text: "Elige una imagen especial, ajusta el encuadre y conviértela en una composición de imanes con presencia, original y totalmente personalizada.",
  imageSrc:
    "/images/frame-gallery/frame-gallery-collage/frame-gallery-collage.3.webp",
  imageAlt: "Colección de imanes personalizados como imagen provisional para collage",
  videoSrc: "/videos/collage.mp4?v=3",
  videoAlt: "Video de collage magnético personalizado",
  link: "/editor",
  cta: "Crear collage",
  layout: ["image", "video", "text"],
};

const OFFER_FEATURE = {
  eyebrow: "Pack edición especial",
  title: "Oferta Pack Recuerdos de Cine",
  text: "Oferta completa con tres imanes personalizados, una tira de negativos y soporte incluidos. Un pack llamativo, listo para sorprender y con precio especial.",
  imageSrc:
    "/images/frame-gallery/frame-gallery-tira-negativos/frame-gallery-tira-negativos-poster.webp",
  imageAlt: "Pack Recuerdos de Cine con tira de negativos e imanes personalizados",
  videoSrc: "/videos/tira-negativos-oferta.mp4",
  videoAlt: "Video del pack Recuerdos de Cine con tira de negativos",
  link: "/ofertas",
  cta: "Ver oferta",
  layout: ["image", "video", "text"],
};

const HERO_CARDS = [
  {
    id: "colecciones",
    eyebrow: "Para regalar",
    title: "Recuerdos con historia",
    text: "Crea pequeñas colecciones con fotos familiares, viajes o momentos especiales.",
    mediaType: "image",
    mediaSrc:
      "/images/frame-gallery/frame-gallery-imanes/iman-personalizado-piscina-slider.webp",
    mediaAlt: "Colección de imanes personalizados listos para regalar",
    link: "/fotos",
  },
  {
    id: "video",
    eyebrow: "Hechos a mano",
    title: "Imanes personalizados",
    text: "Tus fotos favoritas en imanes artesanales, protegidos y listos para regalar.",
    mediaType: "video",
    mediaSrc: "/videos/imanes.mp4",
    mediaPoster:
      "/images/frame-gallery/frame-gallery-imanes/iman-personalizado-piscina-slider.webp",
    mediaAlt: "Video mostrando imanes personalizados con fotos",
    link: "/fotos",
  },
  {
    id: "acabado",
    eyebrow: "Acabado protegido",
    title: "Preparados para durar",
    text: "Cada pieza se fabrica con cuidado y una capa protectora sobre la imagen.",
    mediaType: "image",
    mediaSrc:
      "/images/frame-gallery/frame-gallery-imanes/iman-personalizado-colecciones-slider.webp",
    mediaAlt: "Reverso de imanes personalizados con acabado artesanal",
    link: "/fotos",
  },
];

const CREATION_FEATURES = [OFFER_FEATURE, ...FRAME_FEATURES, COLLAGE_FEATURE];

function renderHeroMedia(card) {
  if (card.mediaType === "video") {
    return (
      <video
        src={card.mediaSrc}
        poster={card.mediaPoster}
        muted
        loop
        autoPlay
        playsInline
        preload="metadata"
        aria-label={card.mediaAlt}
      />
    );
  }

  return <img src={card.mediaSrc} alt={card.mediaAlt} />;
}

function renderFeatureSlot(feature, slot) {
  if (slot === "image") {
    return (
      <Link key="image" to={feature.link} className="home-feature-media home-feature-media--image">
        <img src={feature.imageSrc} alt={feature.imageAlt} loading="lazy" />
      </Link>
    );
  }

  if (slot === "video") {
    return (
      <Link key="video" to={feature.link} className="home-feature-media home-feature-media--video">
        <video
          src={feature.videoSrc}
          muted
          loop
          autoPlay
          playsInline
          preload="metadata"
          aria-label={feature.videoAlt}
        />
      </Link>
    );
  }

  return (
    <div key="text" className="home-feature-content home-feature-content--text">
      <span>{feature.eyebrow}</span>
      <p>{feature.text}</p>
      <Link to={feature.link}>
        {feature.cta}
        {feature.link === "/ofertas" ? (
          <Gift aria-hidden="true" />
        ) : (
          <ArrowRight aria-hidden="true" />
        )}
      </Link>
    </div>
  );
}

export default function HomePage() {
  useEffect(() => {
    if (window.location.hash === "#tienda") {
      window.setTimeout(() => {
        document.getElementById("tienda")?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 300);
    }
  }, []);

  return (
    <div className="home-page">
      <SEO
        title="Regalos personalizados con fotos | Imalandia"
        description="Crea regalos personalizados con tus fotos: imanes, marcos, collages y composiciones artesanales para regalar, decorar y conservar tus recuerdos."
        canonical={`${SITE_URL}/`}
      />
      <StructuredData
        data={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Imalandia",
            url: SITE_URL,
            logo: `${SITE_URL}/images/branding/logo_imalandia.png`,
            contactPoint: {
              "@type": "ContactPoint",
              contactType: "customer service",
              availableLanguage: "es",
              url: `${SITE_URL}/contacto`,
            },
            sameAs: ["https://tiktok.com/@imalandia.com"],
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Imalandia",
            url: SITE_URL,
            inLanguage: "es-ES",
          },
        ]}
      />

      <Header />

      <section className="home-hero">
        <div className="home-hero-content">
          <p className="home-hero-eyebrow">
            Recuerdos para regalar y decorar
          </p>
          <h1>Regalos personalizados con tus fotos</h1>
          <p className="home-hero-text">
            Crea imanes, marcos y composiciones con tus momentos favoritos:
            piezas originales para sorprender o tener tus recuerdos siempre a
            la vista.
          </p>

          <div className="home-hero-actions">
            <Link
              to="/imanes-personalizados-con-fotos"
              className="home-hero-primary"
            >
              Ver imanes personalizados
              <ArrowRight aria-hidden="true" />
            </Link>

            <a href="#home-creation-title" className="home-hero-secondary">
              Explorar creaciones
            </a>
          </div>

          <p className="home-hero-notes">
            Imanes · Marcos · Collages · Packs especiales
          </p>
        </div>

        <div className="home-create-grid home-hero-grid">
          {HERO_CARDS.map((card) => (
            <Link key={card.id} to={card.link} className="home-create-item">
              <div className="home-create-media">{renderHeroMedia(card)}</div>

              <div className="home-create-content">
                <span>{card.eyebrow}</span>
                <h3>{card.title}</h3>
                <p>{card.text}</p>
                <strong>Crear imanes</strong>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="home-creation-showcase" aria-labelledby="home-creation-title">
        <header className="home-band-heading home-creation-heading">
          <p>Escaparate creativo</p>
          <h2 id="home-creation-title">Crea con tus fotos</h2>
          <span>
            Marcos, collage y packs especiales para convertir tus recuerdos en piezas listas para regalar o decorar.
          </span>
        </header>

        <div className="home-creation-list">
          {CREATION_FEATURES.map((feature) => (
            <article key={feature.link} className="home-creation-row">
              <h3 className="home-creation-row-title">{feature.title}</h3>

              <div className="home-feature-row">
                {feature.layout.map((slot) => renderFeatureSlot(feature, slot))}
              </div>
            </article>
          ))}
        </div>
      </section>

      <FeaturedProducts />

      <section id="como-funciona" className="home-section">
        <h2 className="home-section-title">Cómo funciona</h2>

        <div className="home-steps">
          <div className="home-step">
            <strong>1</strong>
            <h3>Elige tu producto</h3>
            <p>
              Escoge entre imanes, collage, marcos o accesorios para tus fotos.
            </p>
          </div>

          <div className="home-step">
            <strong>2</strong>
            <h3>Personaliza tus fotos</h3>
            <p>
              Sube tus imágenes y prueba encuadres, colores, modelos y formatos.
            </p>
          </div>

          <div className="home-step">
            <strong>3</strong>
            <h3>Recibe tu pedido</h3>
            <p>
              Preparamos cada pieza con cuidado para que llegue lista para
              regalar o decorar.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
